import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { StickyNote, Plus, X, Save, Trash2, Pin } from 'lucide-react'
import { db } from '../../firebase'
import { collection, addDoc, onSnapshot, updateDoc, deleteDoc, doc, serverTimestamp, query, where, orderBy } from 'firebase/firestore'

const QuickNotes = ({ clientId, clientName, clientType = 'general' }) => {
  const [notes, setNotes] = useState([])
  const [isAdding, setIsAdding] = useState(false)
  const [newNote, setNewNote] = useState({ title: '', content: '', pinned: false })
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})

  useEffect(() => {
    const notesRef = collection(db, 'quickNotes')
    const q = clientId 
      ? query(notesRef, where('clientId', '==', clientId), orderBy('pinned', 'desc'), orderBy('createdAt', 'desc'))
      : query(notesRef, where('clientType', '==', clientType), orderBy('pinned', 'desc'), orderBy('createdAt', 'desc'))
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate()
      }))
      setNotes(loaded)
    })

    return () => unsubscribe()
  }, [clientId, clientType])

  const handleAddNote = async (e) => {
    e.preventDefault()
    if (!newNote.content.trim()) return

    try {
      await addDoc(collection(db, 'quickNotes'), {
        ...newNote,
        clientId: clientId || null,
        clientName: clientName || 'General',
        clientType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      setNewNote({ title: '', content: '', pinned: false })
      setIsAdding(false)
    } catch (error) {
      console.error('Error adding note:', error)
      alert('❌ Gabim gjatë shtimit të shënimit!')
    }
  }

  const handleUpdateNote = async (id) => {
    try {
      await updateDoc(doc(db, 'quickNotes', id), {
        ...editData,
        updatedAt: serverTimestamp()
      })
      setEditingId(null)
      setEditData({})
    } catch (error) {
      console.error('Error updating note:', error)
      alert('❌ Gabim gjatë përditësimit!')
    }
  }

  const handleTogglePin = async (id, currentPinned) => {
    try {
      await updateDoc(doc(db, 'quickNotes', id), {
        pinned: !currentPinned,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error toggling pin:', error)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm('Dëshiron ta fshish këtë shënim?')) {
      try {
        await deleteDoc(doc(db, 'quickNotes', id))
      } catch (error) {
        console.error('Error deleting note:', error)
      }
    }
  }

  const startEdit = (note) => {
    setEditingId(note.id)
    setEditData({ title: note.title, content: note.content, pinned: note.pinned })
  }

  const getColorClass = (index) => {
    const colors = [
      'from-yellow-500/20 to-yellow-600/20 border-yellow-500/40',
      'from-blue-500/20 to-blue-600/20 border-blue-500/40',
      'from-green-500/20 to-green-600/20 border-green-500/40',
      'from-pink-500/20 to-pink-600/20 border-pink-500/40',
      'from-purple-500/20 to-purple-600/20 border-purple-500/40',
    ]
    return colors[index % colors.length]
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-display font-bold text-gold-400 flex items-center">
          <StickyNote className="w-5 h-5 mr-2" />
          Shënime të Shpejta {notes.length > 0 && `(${notes.length})`}
        </h4>
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="btn-primary text-sm"
        >
          {isAdding ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {isAdding ? 'Anulo' : 'Shto Shënim'}
        </button>
      </div>

      {/* Add Note Form */}
      <AnimatePresence>
        {isAdding && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleAddNote}
            className="bg-gradient-to-br from-gold-500/10 to-bronze-500/10 border border-gold-500/30 rounded-xl p-4 space-y-3"
          >
            <input
              type="text"
              placeholder="Titull (opsional)"
              value={newNote.title}
              onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
              className="w-full px-4 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
            />
            <textarea
              placeholder="Shkruaj shënimin këtu..."
              value={newNote.content}
              onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
              required
              rows={4}
              className="w-full px-4 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none resize-none"
            />
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 text-sm text-beige-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newNote.pinned}
                  onChange={(e) => setNewNote({ ...newNote, pinned: e.target.checked })}
                  className="w-4 h-4 rounded border-gold-500/30 text-gold-500 focus:ring-2 focus:ring-gold-500/20"
                />
                <span className="flex items-center">
                  <Pin className="w-3 h-3 mr-1" />
                  Mbaj në krye
                </span>
              </label>
              <button type="submit" className="btn-primary text-sm">
                <Save className="w-4 h-4 mr-1" />
                Ruaj Shënimin
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Notes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {notes.map((note, index) => (
            <motion.div
              key={note.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`relative bg-gradient-to-br ${getColorClass(index)} backdrop-blur-sm border rounded-xl p-4 shadow-lg hover:shadow-xl transition-all`}
            >
              {/* Pin indicator */}
              {note.pinned && (
                <div className="absolute top-2 right-2">
                  <Pin className="w-4 h-4 text-gold-400 fill-gold-400" />
                </div>
              )}

              {editingId === note.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    placeholder="Titull"
                    className="w-full px-3 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream text-sm"
                  />
                  <textarea
                    value={editData.content}
                    onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream text-sm resize-none"
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center space-x-2 text-xs text-beige-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editData.pinned}
                        onChange={(e) => setEditData({ ...editData, pinned: e.target.checked })}
                        className="w-3 h-3 rounded border-gold-500/30 text-gold-500"
                      />
                      <span>Pin</span>
                    </label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleUpdateNote(note.id)}
                        className="px-3 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-xs"
                      >
                        <Save className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 rounded-lg text-xs"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  {note.title && (
                    <h5 className="font-bold text-cream mb-2 pr-6">{note.title}</h5>
                  )}
                  <p className="text-beige-200 text-sm leading-relaxed whitespace-pre-wrap mb-3">
                    {note.content}
                  </p>
                  <div className="flex items-center justify-between text-xs text-beige-400">
                    <span>
                      {note.createdAt.toLocaleDateString('sq-AL', { 
                        day: '2-digit', 
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleTogglePin(note.id, note.pinned)}
                        className="p-1 hover:bg-gold-500/20 rounded transition-all"
                        title={note.pinned ? 'Unpin' : 'Pin'}
                      >
                        <Pin className={`w-3 h-3 ${note.pinned ? 'fill-gold-400 text-gold-400' : 'text-beige-400'}`} />
                      </button>
                      <button
                        onClick={() => startEdit(note)}
                        className="p-1 hover:bg-blue-500/20 rounded transition-all"
                        title="Edit"
                      >
                        <Save className="w-3 h-3 text-blue-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="p-1 hover:bg-red-500/20 rounded transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {notes.length === 0 && !isAdding && (
        <div className="text-center py-12 bg-bronze-900/20 rounded-2xl">
          <StickyNote className="w-12 h-12 text-beige-400 mx-auto mb-3 opacity-50" />
          <p className="text-beige-300 text-sm">
            Nuk ka shënime ende. Kliko "Shto Shënim" për të filluar!
          </p>
        </div>
      )}
    </div>
  )
}

export default QuickNotes
